import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { http } from "../../../services/HttpService";
import { HTTP_ENDPOINTS } from "../../../consts/api";
import { uiNotification } from "../../../services/UINotificationService";
import { BoomRXPatientValidationModal } from "./BoomRXPatientValidationModal";
import styles from "./ProcedureBoomRXPrescriptions.module.scss";

/** 2-day cold shipping is disabled by WellSync during warmer months; use non-cold 2-day. */
const WELLSYNC_TWO_DAY_METHOD = "SHIPPING_METHOD_NON_COLD_2DAY";
const DEFAULT_SHIPPING_METHOD = WELLSYNC_TWO_DAY_METHOD;
const ALL_SHIPPING_METHODS = [
  WELLSYNC_TWO_DAY_METHOD,
  "SHIPPING_METHOD_OVERNIGHT",
];

const ID_TYPE_OPTIONS = [
  { value: "DriversLicense", label: "Driver's License" },
  { value: "StateIssuedId", label: "State-Issued ID" },
  { value: "SSN", label: "SSN" },
  { value: "Passport", label: "Passport" },
];

function isControlledDrug(drug) {
  if (!drug) return false;
  return !!(
    drug.controlled ||
    drug.isControlled ||
    drug.is_controlled ||
    drug.deaSchedule ||
    drug.schedule
  );
}

function controlledScheduleLabel(drug) {
  return drug.schedule || drug.deaSchedule || drug.dea_schedule || "CS";
}

const SHIPPING_LABEL = {
  SHIPPING_METHOD_3DAY: "3-Day",
  SHIPPING_METHOD_2DAY: "2-Day (Cold — unavailable)",
  SHIPPING_METHOD_OVERNIGHT: "Overnight",
  SHIPPING_METHOD_NON_COLD_2DAY: "2-Day",
};

/** Shown (and used for estimates) when /v1/prices omits or reshapes `shippingFees`. */
const DISPLAY_FALLBACK_SHIPPING_FEES = {
  SHIPPING_METHOD_NON_COLD_2DAY: 29.99,
  SHIPPING_METHOD_OVERNIGHT: 25,
};

function normalizeShippingMethodId(methodId) {
  if (methodId === "SHIPPING_METHOD_2DAY") {
    return WELLSYNC_TWO_DAY_METHOD;
  }
  return methodId;
}

function normalizeAllowedShippingMethods(allowed) {
  const ids = (Array.isArray(allowed) ? allowed : []).map(
    normalizeShippingMethodId,
  );
  return [...new Set(ids)];
}

function wellsyncErrorMessage(err, fallback) {
  const data = err && err.response && err.response.data;
  if (data && data.data && data.data.detail) {
    return data.data.detail;
  }
  if (
    data &&
    data.message &&
    data.message.indexOf("BoomRX API error") === -1 &&
    data.message.indexOf("wellsync_") !== 0
  ) {
    return data.message;
  }
  return fallback;
}

/** First argument that is not null/undefined (replaces ?? for older Babel). */
function firstDefined() {
  for (var i = 0; i < arguments.length; i += 1) {
    var v = arguments[i];
    if (v !== undefined && v !== null) return v;
  }
  return undefined;
}

function coalesceFiniteNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Normalize WellSync price payload `shippingFees` (object map or array rows).
 */
function shippingFeesLookupFromPricesData(priceData) {
  if (!priceData) return {};
  const raw = firstDefined(
    priceData.shippingFees,
    priceData.shipping_fees,
    priceData.shippingFeesByMethod,
  );
  const out = {};
  if (raw == null) return out;
  if (typeof raw === "object" && !Array.isArray(raw)) {
    Object.entries(raw).forEach(([k, v]) => {
      if (k == null || v === "" || v == null) return;
      const n = coalesceFiniteNumber(v);
      if (n != null) out[normalizeShippingMethodId(String(k))] = n;
    });
    return out;
  }
  if (Array.isArray(raw)) {
    raw.forEach((row) => {
      if (!row || typeof row !== "object") return;
      const key = firstDefined(
        row.shippingMethod,
        row.method,
        row.id,
        row.type,
        row.name,
      );
      const val = firstDefined(
        row.fee,
        row.amount,
        row.price,
        row.value,
        row.cost,
      );
      if (key != null) {
        const n = coalesceFiniteNumber(val);
        if (n != null) {
          out[normalizeShippingMethodId(String(key))] = n;
        }
      }
    });
  }
  return out;
}

function formatShippingFee(amount) {
  if (amount == null || Number.isNaN(Number(amount))) return "—";
  return `$${Number(amount).toFixed(2)}`;
}

function formatOrderStatus(status) {
  if (!status) return "";
  return status
    .replace(/^ORDER_STATUS_/, "")
    .replace(/_/g, " ")
    .replace(
      /\w\S*/g,
      (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
    );
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
}

function normalizePhone(phone) {
  const digits = (phone || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  if (digits.length === 10) return `+1${digits}`;
  return (phone || "").trim();
}

function normalizeUsState(state) {
  const v = (state || "").trim();
  if (!v) return "";
  if (v.length === 2) return v.toUpperCase();
  return v.slice(0, 2).toUpperCase();
}

function toIsoDob(dob) {
  if (!dob) return "";
  if (String(dob).includes("T")) return dob;
  return `${dob}T00:00:00Z`;
}

function normalizeCountry(c) {
  const u = (c || "").trim().toUpperCase();
  if (u === "US" || u === "USA" || u === "UNITED STATES") return "US";
  return u || "US";
}

function drugCatalogId(drug) {
  if (!drug || typeof drug !== "object") return "";
  return String(firstDefined(drug.id, drug.drugId, "") || "");
}

/**
 * WellSync CareConnect ordering UI (procedure prescription context).
 * procedurePrescriptionInformationId is optional; the backend creates/links one on order submit when procedureId is sent.
 */
export function ProcedureBoomRXPrescriptions({
  clinicId,
  procedureId,
  procedurePrescriptionInformationId,
  clientId,
  onBack,
  patient,
  prescriber,
  prescriptionLines,
  clinicForShipping,
  allowedShippingMethods,
}) {
  const offeredShippingMethods = useMemo(() => {
    const allowed = normalizeAllowedShippingMethods(
      Array.isArray(allowedShippingMethods)
        ? allowedShippingMethods
        : ALL_SHIPPING_METHODS,
    );
    const filtered = ALL_SHIPPING_METHODS.filter((m) => allowed.includes(m));
    return filtered.length ? filtered : ALL_SHIPPING_METHODS;
  }, [allowedShippingMethods]);

  const [validationOpen, setValidationOpen] = useState(true);
  const [verifiedPatient, setVerifiedPatient] = useState(null);
  const [drugs, setDrugs] = useState([]);
  const [drugsLoading, setDrugsLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [cart, setCart] = useState([]);
  const [prices, setPrices] = useState(null);
  const [shippingMethod, setShippingMethod] = useState(
    () => offeredShippingMethods[0] || DEFAULT_SHIPPING_METHOD,
  );
  const [shipTo, setShipTo] = useState("patient");
  const [shipToConfirm, setShipToConfirm] = useState(null);
  // { next, checking, removed: [{drugName}], kept: cartLines, previewDrugs }
  const cartRef = useRef(cart);
  useEffect(() => {
    cartRef.current = cart;
  }, [cart]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetDrug, setSheetDrug] = useState(null);
  const [sheetDosageId, setSheetDosageId] = useState("");
  const [sheetQty, setSheetQty] = useState(1);
  const [sheetDays, setSheetDays] = useState(30);
  const [sheetRefills, setSheetRefills] = useState(0);
  const [sheetDirections, setSheetDirections] = useState("");
  const [sheetJustification, setSheetJustification] = useState("");
  const [sheetIdType, setSheetIdType] = useState("DriversLicense");
  const [sheetIdNumber, setSheetIdNumber] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const activePatient = verifiedPatient || patient;

  const patientState = useMemo(
    () => normalizeUsState(activePatient?.state),
    [activePatient?.state],
  );

  useEffect(() => {
    const normalized = normalizeShippingMethodId(shippingMethod);
    if (normalized !== shippingMethod) {
      setShippingMethod(normalized);
      return;
    }
    if (!offeredShippingMethods.includes(shippingMethod)) {
      setShippingMethod(offeredShippingMethods[0] || DEFAULT_SHIPPING_METHOD);
    }
  }, [offeredShippingMethods, shippingMethod]);

  const resolvedShippingAddress = useMemo(() => {
    if (shipTo === "clinic" && clinicForShipping) {
      const st = normalizeUsState(clinicForShipping.state);
      return {
        addressLineOne: clinicForShipping.address || "",
        city: clinicForShipping.city || "",
        administrativeArea: st,
        zip: clinicForShipping.zip || "",
        country: normalizeCountry(clinicForShipping.country),
      };
    }
    return {
      addressLineOne: activePatient.address || "",
      city: activePatient.city || "",
      administrativeArea: patientState,
      zip: activePatient.zip || "",
      country: normalizeCountry(activePatient.country),
    };
  }, [shipTo, clinicForShipping, activePatient, patientState]);

  const shippingState = resolvedShippingAddress.administrativeArea;

  const cartHasControlled = useMemo(
    () => cart.some((item) => item.controlled),
    [cart],
  );

  const shippingStateForTarget = useCallback(
    (target) => {
      if (target === "clinic" && clinicForShipping) {
        return normalizeUsState(clinicForShipping.state);
      }
      return patientState;
    },
    [clinicForShipping, patientState],
  );

  const fetchDrugsForState = useCallback(
    async (stateCode) => {
      const res = await http.post(HTTP_ENDPOINTS.boomrx.drugs(), {
        clinicId,
        shippingState: stateCode || undefined,
      });
      const payload = res.data && res.data.data;
      const rawDrugs = payload && payload.drugs;
      const list = rawDrugs != null ? rawDrugs : [];
      return Array.isArray(list) ? list : [];
    },
    [clinicId],
  );

  const loadDrugs = useCallback(async () => {
    setDrugsLoading(true);
    try {
      const list = await fetchDrugsForState(shippingState);
      setDrugs(list);
    } catch (e) {
      uiNotification.error(
        wellsyncErrorMessage(e, "Failed to load BoomRX catalog"),
      );
    } finally {
      setDrugsLoading(false);
    }
  }, [fetchDrugsForState, shippingState]);

  useEffect(() => {
    void loadDrugs();
  }, [loadDrugs]);

  useEffect(() => {
    if (shipTo === "clinic" && !clinicForShipping) {
      setShipTo("patient");
    }
  }, [shipTo, clinicForShipping]);

  const applyShipToChange = useCallback(
    (next, { keptCart, previewDrugs } = {}) => {
      if (Array.isArray(keptCart)) {
        setCart(keptCart);
      }
      if (Array.isArray(previewDrugs)) {
        setDrugs(previewDrugs);
      }
      setShipTo(next);
      setShipToConfirm(null);
    },
    [],
  );

  const requestShipToChange = useCallback(
    async (next) => {
      if (!next || next === shipTo) {
        return;
      }
      if (next === "clinic" && !clinicForShipping) {
        return;
      }
      if (shipToConfirm) {
        return;
      }

      // Always read latest cart — avoids stale empty-cart shortcut.
      const currentCart = Array.isArray(cartRef.current) ? cartRef.current : [];

      // Empty cart — switch immediately (catalog refresh only).
      if (!currentCart.length) {
        applyShipToChange(next);
        return;
      }

      const nextState = shippingStateForTarget(next);
      const currentState = shippingState || "";
      const nextStateLabel = nextState || "the new location";
      const sameState =
        nextState &&
        currentState &&
        nextState.toUpperCase() === currentState.toUpperCase();

      // Cart has items: always confirm before changing Ship to (even same state),
      // so products never disappear without an explicit user action.
      setShipToConfirm({
        next,
        checking: !sameState,
        removed: [],
        kept: currentCart,
        previewDrugs: null,
        nextStateLabel,
        sameState: Boolean(sameState),
      });

      if (sameState) {
        return;
      }

      try {
        const previewDrugs = await fetchDrugsForState(nextState);
        const availableIds = new Set(
          previewDrugs.map((d) => drugCatalogId(d)).filter(Boolean),
        );
        const removed = [];
        const kept = [];
        currentCart.forEach((item) => {
          if (availableIds.has(String(item.drugId))) {
            kept.push(item);
          } else {
            removed.push({
              drugName: item.drugName || "Medication",
              localId: item.localId,
            });
          }
        });

        setShipToConfirm({
          next,
          checking: false,
          removed,
          kept,
          previewDrugs,
          nextStateLabel,
          sameState: false,
        });
      } catch (e) {
        setShipToConfirm(null);
        uiNotification.error(
          wellsyncErrorMessage(
            e,
            "Could not check drug availability for the new shipping address",
          ),
        );
      }
    },
    [
      applyShipToChange,
      clinicForShipping,
      fetchDrugsForState,
      shipTo,
      shipToConfirm,
      shippingState,
      shippingStateForTarget,
    ],
  );

  const cancelShipToChange = useCallback(() => {
    setShipToConfirm(null);
  }, []);

  const confirmShipToChange = useCallback(() => {
    if (!shipToConfirm || shipToConfirm.checking) {
      return;
    }
    const removedCount = Array.isArray(shipToConfirm.removed)
      ? shipToConfirm.removed.length
      : 0;
    applyShipToChange(shipToConfirm.next, {
      keptCart: shipToConfirm.sameState ? cartRef.current : shipToConfirm.kept,
      previewDrugs: shipToConfirm.previewDrugs,
    });
    if (removedCount > 0) {
      uiNotification.info(
        removedCount === 1
          ? "1 medication was removed — it is not available for the new shipping state."
          : `${removedCount} medications were removed — they are not available for the new shipping state.`,
      );
    }
  }, [applyShipToChange, shipToConfirm]);

  const loadPrices = useCallback(async () => {
    try {
      const res = await http.post(HTTP_ENDPOINTS.boomrx.prices(), {
        clinicId,
      });
      const priceData = res.data && res.data.data;
      setPrices(priceData != null ? priceData : null);
    } catch (e) {
      setPrices(null);
      uiNotification.error(
        wellsyncErrorMessage(e, "Failed to load BoomRX prices"),
      );
    }
  }, [clinicId]);

  useEffect(() => {
    void loadPrices();
  }, [loadPrices]);

  const filteredDrugs = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return drugs;
    return drugs.filter((d) => (d.name || "").toLowerCase().includes(q));
  }, [drugs, filter]);

  const openSheet = (drug) => {
    const id = drugCatalogId(drug);
    if (cart.some((c) => String(c.drugId) === id)) return;
    setSheetDrug(drug);
    const first = drug.dosages?.[0];
    setSheetDosageId(first?.id || "");
    setSheetQty(parseInt(first?.defaultQuantity, 10) || 1);
    setSheetDays(30);
    setSheetRefills(0);
    setSheetDirections(first?.directions || "");
    setSheetJustification("");
    setSheetIdType("DriversLicense");
    setSheetIdNumber("");
    setSheetOpen(true);
  };

  const selectedDosage = useMemo(() => {
    if (!sheetDrug?.dosages) return null;
    return sheetDrug.dosages.find((d) => d.id === sheetDosageId) || null;
  }, [sheetDrug, sheetDosageId]);

  const addToCart = () => {
    if (!sheetDrug || !selectedDosage) {
      uiNotification.error("Select a dosage");
      return;
    }
    if (!sheetDirections.trim()) {
      uiNotification.error("Directions are required");
      return;
    }
    if (!sheetJustification.trim()) {
      uiNotification.error("Clinical justification is required");
      return;
    }
    const controlled = isControlledDrug(sheetDrug);
    if (controlled && !sheetIdNumber.trim()) {
      uiNotification.error("ID number is required for controlled substances");
      return;
    }
    const line = {
      localId: `${Date.now()}-${Math.random()}`,
      drugId: drugCatalogId(sheetDrug),
      dosageId: selectedDosage.id,
      drugName: sheetDrug.name,
      dosage: selectedDosage.concentration,
      quantity: sheetQty,
      daysSupply: sheetDays,
      refills: sheetRefills,
      directions: sheetDirections.trim(),
      justification: sheetJustification.trim(),
      controlled,
      idVerification: controlled
        ? { idType: sheetIdType, idNumber: sheetIdNumber.trim() }
        : null,
    };
    setCart((c) => [...c, line]);
    setSheetOpen(false);
  };

  const removeLine = (localId) => {
    setCart((c) => c.filter((x) => x.localId !== localId));
  };

  const shippingFeeByMethod = useMemo(() => {
    const fromApi = shippingFeesLookupFromPricesData(prices || {});
    const out = {};
    offeredShippingMethods.forEach((methodId) => {
      const v = fromApi[methodId];
      out[methodId] =
        v != null
          ? v
          : DISPLAY_FALLBACK_SHIPPING_FEES[methodId] != null
          ? DISPLAY_FALLBACK_SHIPPING_FEES[methodId]
          : null;
    });
    return out;
  }, [prices, offeredShippingMethods]);

  const shippingTotal = useMemo(() => {
    if (!cart.length) return null;
    const ship = shippingFeeByMethod[shippingMethod];
    return ship != null ? ship : null;
  }, [cart.length, shippingFeeByMethod, shippingMethod]);

  const orderHistory = useMemo(() => {
    const lines = Array.isArray(prescriptionLines) ? prescriptionLines : [];
    const wsLines = lines.filter((l) => l.wellsync_order_id);
    const grouped = {};
    wsLines.forEach((l) => {
      const key = l.wellsync_order_id;
      if (!grouped[key]) {
        grouped[key] = {
          orderId: key,
          status: l.wellsync_order_status || "",
          shipping: l.wellsync_shipping_method || "",
          date: l.created || l.modified || "",
          drugs: [],
        };
      }
      grouped[key].drugs.push(l.medicine_name || l.wellsync_drug_id || "");
    });
    return Object.values(grouped).sort(
      (a, b) => new Date(b.date) - new Date(a.date),
    );
  }, [prescriptionLines]);

  const [trackingOrderId, setTrackingOrderId] = useState(null);

  const trackOrder = async (orderId) => {
    setTrackingOrderId(orderId);
    try {
      const res = await http.post(HTTP_ENDPOINTS.boomrx.ordersTrack(), {
        clinicId,
        orderId,
      });
      const items = res.data?.data?.prescriptionItems;
      if (Array.isArray(items) && items.length > 0) {
        const newStatus = items[0].status || items[0].orderStatus;
        if (newStatus) {
          uiNotification.success("Status: " + formatOrderStatus(newStatus));
        } else {
          uiNotification.success("Tracking data received");
        }
      } else {
        uiNotification.info("No tracking updates available");
      }
    } catch (e) {
      uiNotification.error(
        e.response?.data?.message || "Failed to track order",
      );
    } finally {
      setTrackingOrderId(null);
    }
  };

  const handleSubmit = async () => {
    if (!verifiedPatient) {
      uiNotification.error("Confirm patient details before submitting");
      setValidationOpen(true);
      return;
    }
    if (!activePatient?.firstName || !activePatient?.lastName) {
      uiNotification.error("Patient name is required");
      return;
    }
    if (shipTo === "patient" && (!patientState || patientState.length !== 2)) {
      uiNotification.error("Patient state must be a valid 2-letter code");
      return;
    }
    if (shipTo === "clinic" && clinicForShipping) {
      const st = normalizeUsState(clinicForShipping.state);
      if (!st || st.length !== 2) {
        uiNotification.error(
          "Clinic address must include a valid 2-letter state",
        );
        return;
      }
    }
    const shipAddr = resolvedShippingAddress;
    if (
      !shipAddr.administrativeArea ||
      shipAddr.administrativeArea.length !== 2
    ) {
      uiNotification.error(
        "Shipping address must include a valid 2-letter state",
      );
      return;
    }
    if (!prescriber?.npi || !/^\d{10}$/.test(String(prescriber.npi).trim())) {
      uiNotification.error("A valid 10-digit prescriber NPI is required");
      return;
    }
    const clinicianExternalId = String(prescriber.externalId || "").trim();
    if (!clinicianExternalId) {
      uiNotification.error(
        "Prescriber is missing an id. Re-open the procedure and try again.",
      );
      return;
    }
    if (!prescriber?.phone?.trim()) {
      uiNotification.error("Prescriber phone is required");
      return;
    }
    if (!cart.length) {
      uiNotification.error("Add at least one medication");
      return;
    }

    setSubmitting(true);
    try {
      const externalCaseId = `ar-ws-${procedureId}-${Date.now()}`;
      const orderRes = await http.post(HTTP_ENDPOINTS.boomrx.orders(), {
        clinicId,
        procedureId,
        externalCaseId,
        patient: {
          externalId: String(clientId),
          firstName: activePatient.firstName,
          middleName: activePatient.middleName || "",
          lastName: activePatient.lastName,
          dateOfBirth: activePatient.dob || toIsoDob(activePatient.dobDisplay),
          gender: activePatient.gender || "",
          sexAtBirth: activePatient.sexAtBirth || "",
          email: activePatient.email || "",
          phone: normalizePhone(activePatient.phone),
          primaryAddress: {
            addressLineOne: activePatient.address || "",
            addressLineTwo: activePatient.addressLine2 || "",
            city: activePatient.city || "",
            administrativeArea: patientState,
            zip: activePatient.zip || "",
            country: normalizeCountry(activePatient.country),
          },
        },
        clinician: {
          // Must be the procedure provider AR user id (stable). CareConnect rejects
          // CLINICIAN-ALREADY-EXISTS when the same email is sent with a different externalId.
          externalId: clinicianExternalId,
          npi: String(prescriber.npi).trim(),
          firstName: prescriber.firstName || "",
          lastName: prescriber.lastName || "",
          phone: normalizePhone(prescriber.phone),
          email: prescriber.email || "",
        },
        shippingMethod: normalizeShippingMethodId(shippingMethod),
        shipTo,
        prescriptionItems: cart.map((item) => {
          const payload = {
            drugId: item.drugId,
            dosageId: item.dosageId,
            quantity: item.quantity,
            directions: item.directions,
            daysSupply: item.daysSupply,
            totalRefills: item.refills,
            justification: item.justification,
          };
          if (item.idVerification) {
            payload.patientIdVerification = item.idVerification;
          }
          return payload;
        }),
      });

      const orderPayload = orderRes.data?.data;
      const orderId = orderPayload?.orderId;
      const status = orderPayload?.status;
      const rawPrescriptionInfoId =
        orderPayload?.procedurePrescriptionInformationId;
      const prescriptionInfoId =
        rawPrescriptionInfoId != null
          ? rawPrescriptionInfoId
          : procedurePrescriptionInformationId != null
          ? procedurePrescriptionInformationId
          : 0;

      await http.post(HTTP_ENDPOINTS.boomrx.prescriptionLinesCreate(), {
        clinicId,
        procedureId,
        procedurePrescriptionInformationId: prescriptionInfoId,
        lines: cart.map((item) => ({
          medicine_name: item.drugName,
          dosage: item.dosage,
          quantity: String(item.quantity),
          refills: String(item.refills),
          wellsync_drug_id: item.drugId,
          wellsync_shipping_method: shippingMethod,
          wellsync_order_id: orderId,
          wellsync_order_status: status,
        })),
      });

      uiNotification.success("Order submitted to BoomRX");
      setCart([]);
    } catch (e) {
      uiNotification.error(wellsyncErrorMessage(e, "Order failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const prescriberDisplay =
    [prescriber?.firstName, prescriber?.lastName].filter(Boolean).join(" ") ||
    "Prescriber";
  const patientRibbonName =
    verifiedPatient?.fullName ||
    [activePatient?.firstName, activePatient?.lastName]
      .filter(Boolean)
      .join(" ");

  if (!verifiedPatient) {
    return (
      <BoomRXPatientValidationModal
        open={validationOpen}
        patient={patient}
        onCancel={onBack}
        onVerified={(p) => {
          setVerifiedPatient(p);
          setValidationOpen(false);
        }}
      />
    );
  }

  return (
    <div className={styles.app}>
      <BoomRXPatientValidationModal
        open={validationOpen}
        patient={activePatient}
        onCancel={() => setValidationOpen(false)}
        onVerified={(p) => {
          setVerifiedPatient(p);
          setValidationOpen(false);
        }}
      />

      <div className={styles.ribbon}>
        <button
          type="button"
          className={styles.closeBtn}
          onClick={onBack}
          aria-label="Close"
        >
          ×
        </button>
        <div className={styles.ribbonContent}>
          <div>
            <div className={styles.ribbonLabel}>Patient</div>
            <div className={styles.ribbonPrimary}>{patientRibbonName}</div>
            <div className={styles.ribbonSecondary}>
              {verifiedPatient.phone}
              <span className={styles.dot}>·</span>
              {verifiedPatient.email}
            </div>
          </div>
          <div>
            <div className={styles.ribbonLabel}>Prescriber</div>
            <div className={styles.ribbonPrimary}>{prescriberDisplay}</div>
            <div className={styles.ribbonSecondary}>
              NPI {prescriber?.npi || "—"}
              <span className={styles.dot}>·</span>
              BoomRX Prescriptions
            </div>
          </div>
        </div>
        <div className={styles.ribbonStatus}>
          <div className={styles.verifiedBadge}>✓ Patient verified</div>
        </div>
      </div>

      <div className={styles.main}>
        <div className={styles.catalog}>
          <div className={styles.searchRow}>
            <input
              className={styles.search}
              placeholder="Search drugs by name…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
            <button
              type="button"
              className={styles.refreshBtn}
              onClick={() => void loadDrugs()}
              disabled={drugsLoading}
            >
              Refresh
            </button>
          </div>
          <div className={styles.catalogMeta}>
            <span>
              {filteredDrugs.length} of {drugs.length} drugs
            </span>
            <span>Sorted A–Z</span>
          </div>
          {drugsLoading ? (
            <p className={styles.muted}>Loading catalog…</p>
          ) : (
            <ul className={styles.drugList}>
              {filteredDrugs.map((d) => {
                const id = drugCatalogId(d);
                const added = cart.some((c) => String(c.drugId) === id);
                const controlled = isControlledDrug(d);
                return (
                  <li
                    key={id || d.name}
                    className={
                      added
                        ? `${styles.drugRow} ${styles.drugRowAdded}`
                        : styles.drugRow
                    }
                  >
                    <div className={styles.drugName}>
                      <span>{d.name}</span>
                      {controlled ? (
                        <span className={styles.csPill}>
                          {controlledScheduleLabel(d)}
                        </span>
                      ) : null}
                    </div>
                    {added ? (
                      <span className={styles.addedTag}>✓ Added</span>
                    ) : (
                      <button
                        type="button"
                        className={styles.addBtn}
                        onClick={() => openSheet(d)}
                      >
                        + Add
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className={styles.orderPanel}>
          <div className={styles.orderHeader}>
            Order <span className={styles.orderCount}>{cart.length} Rxs</span>
          </div>
          {cart.length === 0 ? (
            <div className={styles.orderEmpty}>
              <div className={styles.emptyPrimary}>No prescriptions yet</div>
              <div className={styles.emptySecondary}>
                Add a medication from the catalog to begin.
              </div>
            </div>
          ) : (
            cart.map((line) => (
              <div key={line.localId} className={styles.orderItem}>
                <div>
                  <div className={styles.itemName}>{line.drugName}</div>
                  <div className={styles.itemDetails}>
                    {line.dosage}
                    <span className={styles.sep}>·</span>Qty {line.quantity}
                    <span className={styles.sep}>·</span>
                    {line.daysSupply}d<span className={styles.sep}>·</span>
                    {line.refills === 0 || line.refills === "0"
                      ? "no refills"
                      : `${line.refills} refills`}
                  </div>
                </div>
                <button
                  type="button"
                  className={styles.itemRemove}
                  onClick={() => removeLine(line.localId)}
                  aria-label={`Remove ${line.drugName}`}
                >
                  ×
                </button>
              </div>
            ))
          )}

          <div className={styles.sectionLabel}>Ship to</div>
          <p className={styles.shippingNote}>
            BoomRX only ships to the patient address or the clinic address. Drug
            availability is based on the shipping state.
          </p>
          <select
            className={styles.shipSelect}
            value={shipTo}
            disabled={Boolean(shipToConfirm)}
            onChange={(e) => {
              const next = e.target.value;
              // Keep controlled value on current shipTo until user confirms.
              e.target.value = shipTo;
              void requestShipToChange(next);
            }}
          >
            <option value="patient">Patient ({patientRibbonName})</option>
            {clinicForShipping?.displayName ? (
              <option value="clinic">
                Clinic ({clinicForShipping.displayName})
              </option>
            ) : null}
          </select>
          <div className={styles.shipAddress}>
            <div>
              {resolvedShippingAddress.addressLineOne}
              {shipTo === "patient" && activePatient?.addressLine2
                ? `, ${activePatient.addressLine2}`
                : ""}
              <br />
              {resolvedShippingAddress.city},{" "}
              {resolvedShippingAddress.administrativeArea}{" "}
              {resolvedShippingAddress.zip}
              {cartHasControlled ? (
                <div className={styles.shipContact}>
                  <div>
                    <span className={styles.contactKey}>Phone</span>{" "}
                    {verifiedPatient.phone}
                  </div>
                  <div>
                    <span className={styles.contactKey}>Email</span>{" "}
                    {verifiedPatient.email}
                  </div>
                  <div className={styles.contactHelper}>
                    Required for controlled substance delivery verification.
                  </div>
                </div>
              ) : null}
            </div>
            {shipTo === "patient" ? (
              <button
                type="button"
                className={styles.editLink}
                onClick={() => setValidationOpen(true)}
              >
                Edit
              </button>
            ) : null}
          </div>

          {cart.length > 0 ? (
            <div className={styles.shippingBlock}>
              <div className={styles.sectionLabel}>Shipping method</div>
              <p className={styles.shippingNote}>
                Transit time only. Pharmacy processing may take 3–5 business
                days. No weekend shipping. 2-day cold shipping is temporarily
                unavailable — non-cold 2-day is offered when enabled in setup.
              </p>
              <div
                className={styles.shipOptions}
                role="radiogroup"
                aria-label="Shipping method"
              >
                {offeredShippingMethods.map((methodId) => {
                  const fee = shippingFeeByMethod[methodId];
                  const label = SHIPPING_LABEL[methodId] || methodId;
                  const selected = shippingMethod === methodId;
                  return (
                    <div
                      key={methodId}
                      role="radio"
                      tabIndex={0}
                      aria-checked={selected}
                      className={
                        selected
                          ? `${styles.shipOption} ${styles.shipOptionSelected}`
                          : styles.shipOption
                      }
                      onClick={() => setShippingMethod(methodId)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setShippingMethod(methodId);
                        }
                      }}
                    >
                      <input
                        type="radio"
                        name="wellsync-shipping-method"
                        className={styles.shipOptionHiddenInput}
                        checked={selected}
                        readOnly
                        tabIndex={-1}
                      />
                      <span className={styles.shipRadio} />
                      <span className={styles.shipOptionLabel}>{label}</span>
                      <span className={styles.shipOptionPrice}>
                        {formatShippingFee(fee)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
          {cart.length > 0 && shippingTotal != null ? (
            <div className={styles.totals}>
              <div className={styles.totalsRow}>
                <span>Shipping</span>
                <span>{formatShippingFee(shippingTotal)}</span>
              </div>
              <div className={`${styles.totalsRow} ${styles.totalsTotal}`}>
                <span>Total</span>
                <span>{formatShippingFee(shippingTotal)}</span>
              </div>
            </div>
          ) : null}
          <button
            type="button"
            className={styles.submitBtn}
            onClick={() => void handleSubmit()}
            disabled={submitting || !cart.length}
          >
            {submitting ? "Sending…" : "Submit to BoomRX"}
          </button>
        </div>
      </div>

      {orderHistory.length > 0 && (
        <div className={styles.historySection}>
          <h4 className={styles.historyTitle}>ORDER HISTORY</h4>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>PRESCRIPTION</th>
                <th>DRUGS</th>
                <th>FULFILLMENT</th>
                <th>STATUS</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {orderHistory.map((order) => (
                <tr key={order.orderId} className={styles.historyRow}>
                  <td>
                    <div>{formatDate(order.date)}</div>
                    <div className={styles.orderId}>{order.orderId}</div>
                  </td>
                  <td>{order.drugs.join(", ")}</td>
                  <td>{SHIPPING_LABEL[order.shipping] || order.shipping}</td>
                  <td>{formatOrderStatus(order.status)}</td>
                  <td>
                    <button
                      type="button"
                      className={styles.btnIcon}
                      title="Refresh status"
                      disabled={trackingOrderId === order.orderId}
                      onClick={() => void trackOrder(order.orderId)}
                    >
                      &#x21BB;
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className={styles.muted}>
            Click a row to view prescription details.
          </p>
        </div>
      )}

      {shipToConfirm
        ? createPortal(
            <div
              className={styles.modalOverlay}
              role="dialog"
              aria-modal="true"
              style={{
                position: "fixed",
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                zIndex: 2147483000,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(17, 24, 39, 0.55)",
                padding: 24,
              }}
            >
              <div
                className={styles.confirmModal}
                style={{
                  background: "#fff",
                  borderRadius: 8,
                  width: "100%",
                  maxWidth: 480,
                  maxHeight: "90vh",
                  overflowY: "auto",
                  boxShadow: "0 20px 50px rgba(17, 24, 39, 0.25)",
                }}
              >
                <div className={styles.modalHeader}>
                  <div>
                    <div className={styles.modalTitle}>
                      Change shipping destination?
                    </div>
                    <div className={styles.modalSubtitle}>
                      {shipToConfirm.checking
                        ? "Checking which medications are available for the new address…"
                        : shipToConfirm.removed.length > 0
                        ? `Some medications in this order are not available in ${shipToConfirm.nextStateLabel}.`
                        : shipToConfirm.sameState
                        ? "Your order will be kept. Shipping address will update."
                        : `Drug availability will refresh for ${shipToConfirm.nextStateLabel}.`}
                    </div>
                  </div>
                  {!shipToConfirm.checking ? (
                    <button
                      type="button"
                      className={styles.modalClose}
                      onClick={cancelShipToChange}
                      aria-label="Close"
                    >
                      ×
                    </button>
                  ) : null}
                </div>
                <div className={styles.modalBody}>
                  {shipToConfirm.checking ? (
                    <p className={styles.muted}>Please wait…</p>
                  ) : shipToConfirm.removed.length > 0 ? (
                    <>
                      <p className={styles.confirmLead}>
                        If you continue, these medications will be removed from
                        the order:
                      </p>
                      <ul className={styles.confirmList}>
                        {shipToConfirm.removed.map((item) => (
                          <li key={item.localId}>{item.drugName}</li>
                        ))}
                      </ul>
                      {shipToConfirm.kept.length > 0 ? (
                        <p className={styles.confirmKeep}>
                          {shipToConfirm.kept.length} medication
                          {shipToConfirm.kept.length === 1 ? "" : "s"} will stay
                          in the order.
                        </p>
                      ) : (
                        <p className={styles.confirmKeep}>
                          The order will be empty after this change.
                        </p>
                      )}
                    </>
                  ) : (
                    <p className={styles.confirmLead}>
                      {shipToConfirm.sameState
                        ? "All medications will stay in the order."
                        : "All medications in this order are available for the new address and will stay in the order."}
                    </p>
                  )}
                </div>
                <div className={styles.modalFooter}>
                  <button
                    type="button"
                    className={styles.btnSecondary}
                    onClick={cancelShipToChange}
                    disabled={shipToConfirm.checking}
                  >
                    Keep current ship to
                  </button>
                  <button
                    type="button"
                    className={styles.btnPrimary}
                    onClick={confirmShipToChange}
                    disabled={shipToConfirm.checking}
                  >
                    {shipToConfirm.removed.length > 0
                      ? "Remove and continue"
                      : "Continue"}
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}

      {sheetOpen && sheetDrug ? (
        <div className={styles.modalOverlay}>
          <div className={styles.rxModal}>
            <div className={styles.modalHeader}>
              <div>
                <div className={styles.modalTitle}>{sheetDrug.name}</div>
                <div className={styles.modalSubtitle}>New Prescription</div>
              </div>
              <button
                type="button"
                className={styles.modalClose}
                onClick={() => setSheetOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  Dosage<span className={styles.requiredMark}>*</span>
                </label>
                <select
                  className={styles.formSelect}
                  value={sheetDosageId}
                  onChange={(e) => {
                    setSheetDosageId(e.target.value);
                    const d = sheetDrug.dosages?.find(
                      (x) => x.id === e.target.value,
                    );
                    if (d) {
                      setSheetQty(parseInt(d.defaultQuantity, 10) || 1);
                      setSheetDirections(d.directions || "");
                    }
                  }}
                >
                  {(sheetDrug.dosages || []).map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.concentration}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    Quantity<span className={styles.requiredMark}>*</span>
                  </label>
                  <input
                    className={styles.formInput}
                    type="number"
                    min={1}
                    value={sheetQty}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, "");
                      setSheetQty(v === "" ? "" : Number(v));
                    }}
                    onBlur={() => {
                      if (!sheetQty) setSheetQty(1);
                    }}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    Days Supply<span className={styles.requiredMark}>*</span>
                  </label>
                  <input
                    className={styles.formInput}
                    type="number"
                    min={1}
                    value={sheetDays}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, "");
                      setSheetDays(v === "" ? "" : Number(v));
                    }}
                    onBlur={() => {
                      if (!sheetDays) setSheetDays(1);
                    }}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    Refills<span className={styles.requiredMark}>*</span>
                  </label>
                  <input
                    className={styles.formInput}
                    type="number"
                    min={0}
                    value={sheetRefills}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, "");
                      setSheetRefills(v === "" ? "" : Number(v));
                    }}
                    onBlur={() => {
                      if (sheetRefills === "" || sheetRefills === null)
                        setSheetRefills(0);
                    }}
                  />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  Directions (SIG)<span className={styles.requiredMark}>*</span>
                </label>
                <textarea
                  className={styles.formTextarea}
                  rows={3}
                  value={sheetDirections}
                  onChange={(e) => setSheetDirections(e.target.value)}
                  placeholder="e.g., Apply 1mL to inner forearm once daily."
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  Justification<span className={styles.requiredMark}>*</span>
                </label>
                <textarea
                  className={styles.formTextarea}
                  rows={2}
                  value={sheetJustification}
                  onChange={(e) => setSheetJustification(e.target.value)}
                  placeholder="Clinical justification."
                />
              </div>
              {isControlledDrug(sheetDrug) ? (
                <div className={styles.controlledSection}>
                  <div className={styles.controlledHeader}>
                    <span aria-hidden="true">🔒</span>
                    <span>Patient ID Verification — Required</span>
                  </div>
                  <p className={styles.controlledHelper}>
                    This drug is a controlled substance. Per DEA regulations,
                    patient identity must be verified with one of the following.
                  </p>
                  <div className={styles.idRow}>
                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>
                        ID Type<span className={styles.requiredMark}>*</span>
                      </label>
                      <select
                        className={styles.formSelect}
                        value={sheetIdType}
                        onChange={(e) => setSheetIdType(e.target.value)}
                      >
                        {ID_TYPE_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>
                        ID Number<span className={styles.requiredMark}>*</span>
                      </label>
                      <input
                        className={styles.formInput}
                        value={sheetIdNumber}
                        onChange={(e) => setSheetIdNumber(e.target.value)}
                        placeholder="e.g., FL:Z1234567"
                      />
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
            <div className={styles.modalFooter}>
              <button
                type="button"
                className={styles.btnCancel}
                onClick={() => setSheetOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={addToCart}
              >
                Add to cart
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default ProcedureBoomRXPrescriptions;
